import rafSchedule from 'raf-schd';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { getInlineNodeViewProducer } from '@atlaskit/editor-common/getInlineNodeViewProducer';
import { isSSR } from '@atlaskit/editor-common/is-ssr';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { DATASOURCE_INNER_CONTAINER_CLASSNAME } from '@atlaskit/editor-common/styles/smartCard';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types/plugin-factory';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { DATASOURCE_DEFAULT_LAYOUT } from '@atlaskit/linking-common/constants';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { cardPlugin } from '../index';
import { blockCardNodeView } from '../nodeviews/blockCard';
import { createEmbedCardNodeView } from '../nodeviews/createEmbedCardNodeView';
import { InlineCardNodeView } from '../nodeviews/inlineCard';
import { lazyInlineCardView } from '../nodeviews/lazy-inline-card';
import type { CardPluginOptions, CardPluginState } from '../types';
import { eventsFromTransaction } from '../ui/analytics/events-from-tr';
import { isDatasourceTableLayout } from '../ui/LayoutButton/utils';
import { isLocalStorageKeyDiscovered } from '../ui/local-storage';
import {
	clearOverlayCandidate,
	setCardLayoutAndDatasourceTableRef,
	setDatasourceTableRef,
} from './actions';
import { pluginKey } from './plugin-key';
import reducer from './reducers';
import {
	getPastedInlineCards,
	getResolvedPastedInlineCards,
	withPastedInlineLinks,
} from './util/pasted-inline-link';
import { handleProvider, resolveWithProvider } from './util/resolve';
import { getNewRequests, getPluginState, getPluginStateWithUpdatedPos } from './util/state';
import { isBlockSupportedAtPosition, isEmbedSupportedAtPosition } from './utils';

const LOCAL_STORAGE_DISCOVERY_KEY_SMART_LINK = 'smart-link-upgrade-pulse';
// Only the first resolved inline smart link is needed for PO spotlight targeting
const MAX_RESOLVED_INLINE_SMART_LINKS = 1;

const handleAwarenessOverlay = (view: EditorView): void => {
	if (!isExperimentEnabled('platform_editor_reduce_forced_layout_2')) {
		const currentState = getPluginState(view.state);
		const overlayCandidatePos = currentState?.overlayCandidatePosition;
		if (overlayCandidatePos) {
			currentState.removeOverlay?.();
			const tr = view.state.tr;
			clearOverlayCandidate(tr);
			view.dispatch(tr);
		}
	}
};

export const createPlugin =
	(
		options: CardPluginOptions,
		pluginInjectionApi: ExtractInjectionAPI<typeof cardPlugin> | undefined,
	) =>
	(pmPluginFactoryParams: PMPluginFactoryParams): SafePlugin<CardPluginState> => {
		const {
			editorAppearance,
			allowResizing,
			useAlternativePreloader,
			fullWidthMode,
			actionOptions,
			cardPluginEvents,
			trackPastedInlineLinks,
			showUpgradeDiscoverability,
			allowEmbeds,
			allowBlockCards,
			onClickCallback,
			isPageSSRed,
			provider,
			CompetitorPrompt,
			smartCardContext,
			embedCardTransformers,
		} = options;

		const intl = pmPluginFactoryParams.getIntl();

		const enableInlineUpgradeFeatures = !!showUpgradeDiscoverability;

		const inlineCardViewProducer = getInlineNodeViewProducer({
			pmPluginFactoryParams,
			Component: InlineCardNodeView,
			extraComponentProps: {
				useAlternativePreloader,
				actionOptions,
				enableInlineUpgradeFeatures,
				allowEmbeds,
				allowBlockCards,
				pluginInjectionApi,
				onClickCallback,
				isPageSSRed,
				provider,
				CompetitorPrompt: isSSR() ? undefined : CompetitorPrompt,
				intl,
				smartCardContext,
			},
		});

		return new SafePlugin({
			state: {
				init(): CardPluginState {
					return {
						requests: [],
						...(trackPastedInlineLinks ? { pastedInlineLinks: [] } : {}),
						provider: null,
						cards: [],
						datasourceStash: {},
						resolvedToolbarAttributesByUrl: {},
						showLinkingToolbar: false,
						smartLinkEvents: undefined,
						editorAppearance,
						embedCardTransformers,
						showDatasourceModal: false,
						datasourceModalType: undefined,
						datasourceTableRef: undefined,
						layout: undefined,
					};
				},

				apply(tr, pluginState: CardPluginState, prevEditorState: EditorState) {
					// Update all the positions of outstanding requests and
					// cards in the plugin state.
					let pluginStateWithUpdatedPos = getPluginStateWithUpdatedPos(pluginState, tr);
					// Pasted inline cards that already exist as nodes (copy and paste of cards).
					pluginStateWithUpdatedPos = withPastedInlineLinks(
						pluginStateWithUpdatedPos,
						trackPastedInlineLinks ? getPastedInlineCards(tr) : [],
					);

					// apply any actions
					const meta = tr.getMeta(pluginKey);

					if (cardPluginEvents) {
						const events = eventsFromTransaction(tr, prevEditorState);
						cardPluginEvents.push(...events);
					}

					if (!meta) {
						if (pluginState.datasourceTableRef) {
							if (!(tr.selection instanceof NodeSelection) || !tr.selection.node.attrs.datasource) {
								// disable resize button when switching from datasource to block card
								return {
									...pluginStateWithUpdatedPos,
									datasourceTableRef: undefined,
								};
							}
						}
					}

					if (!meta) {
						return pluginStateWithUpdatedPos;
					}

					let newState = reducer(pluginStateWithUpdatedPos, meta);
					// Track the first resolved inline smart link for PO spotlight DOM targeting
					if (meta.type === 'RESOLVE' && pluginState?.requests?.length) {
						const resolvedRequest = pluginState.requests.find((req) => req.url === meta.url);
						if (resolvedRequest?.appearance === 'inline') {
							if (
								(newState.resolvedInlineSmartLinks?.length ?? 0) < MAX_RESOLVED_INLINE_SMART_LINKS
							) {
								newState.resolvedInlineSmartLinks = [
									...(newState.resolvedInlineSmartLinks ?? []),
									{
										pos: resolvedRequest.pos,
										url: resolvedRequest.url,
										source: resolvedRequest.source,
									},
								];
							}
						}
					}

					// Pasted URLs that were converted to inline cards.
					if (trackPastedInlineLinks && meta.type === 'RESOLVE') {
						newState = withPastedInlineLinks(
							newState,
							getResolvedPastedInlineCards(
								tr,
								pluginState.requests,
								meta.url,
								INPUT_METHOD.CLIPBOARD,
							),
						);
					}

					if (!enableInlineUpgradeFeatures) {
						return newState;
					}

					// the code below is related to the "Inline Switcher" project, for more information pls see EDM-7984
					const isSingleInlineLink =
						pluginState?.requests?.length === 1 && pluginState.requests[0].appearance === 'inline';

					const isSmartLinkPulseDiscovered = isLocalStorageKeyDiscovered(
						LOCAL_STORAGE_DISCOVERY_KEY_SMART_LINK,
					);

					if (meta.type !== 'RESOLVE' || !isSingleInlineLink) {
						return newState;
					}

					const linkPosition = pluginState.requests[0].pos;
					const canBeUpgradedToBlock =
						allowBlockCards && isBlockSupportedAtPosition(linkPosition, prevEditorState, 'inline');
					const canBeUpgradedToEmbed =
						allowEmbeds && isEmbedSupportedAtPosition(linkPosition, prevEditorState, 'inline');

					if (
						!isExperimentEnabled('platform_editor_reduce_forced_layout_2') &&
						(canBeUpgradedToBlock || canBeUpgradedToEmbed)
					) {
						newState.overlayCandidatePosition = linkPosition;
					}

					if (!isSmartLinkPulseDiscovered && canBeUpgradedToEmbed) {
						newState.inlineCardAwarenessCandidatePosition = linkPosition;
					}

					return newState;
				},
			},

			filterTransaction(tr: Transaction) {
				const isOutsideClicked = tr.getMeta('outsideProsemirrorEditorClicked');

				if (isOutsideClicked) {
					const isInlineEditingActive = document.getElementById('sllv-active-inline-edit');
					if (isInlineEditingActive) {
						return false;
					}
				}
				return true;
			},

			view(view: EditorView) {
				const domAtPos = view.domAtPos.bind(view);
				const rafCancellationCallbacks: Function[] = [];

				if (options.provider) {
					handleProvider('cardProvider', options.provider, view);
				}

				return {
					update(view: EditorView, prevState: EditorState) {
						const currentState = getPluginState(view.state);
						const oldState = getPluginState(prevState);

						const { state, dispatch } = view;
						const { selection, tr, schema } = state;

						const isBlockCardSelected =
							selection instanceof NodeSelection && selection.node?.type === schema.nodes.blockCard;

						if (isBlockCardSelected) {
							// Ignored via go/ees005
							// eslint-disable-next-line @atlaskit/editor/no-as-casting
							const datasourceTableRef = // Ignored via go/ees005
								// eslint-disable-next-line @atlaskit/editor/no-as-casting
								(findDomRefAtPos(selection.from, domAtPos) as HTMLElement)?.querySelector(
									`.${DATASOURCE_INNER_CONTAINER_CLASSNAME}`,
								) as HTMLElement;

							const { node } = selection;

							const isDatasource = !!node?.attrs?.datasource;

							const shouldUpdateTableRef =
								datasourceTableRef && currentState?.datasourceTableRef !== datasourceTableRef;

							if (isDatasource && shouldUpdateTableRef) {
								// since we use the plugin state, which is a shared state, we need to update the datasourceTableRef, layout on each selection
								const layout = isDatasourceTableLayout(node.attrs.layout)
									? node.attrs.layout
									: DATASOURCE_DEFAULT_LAYOUT;

								const isNested = selection.$anchor.depth > 0;

								// we want to disable resize button when datasource table is nested by not setting then datasourceTableRef on selection
								if (!isNested) {
									// we use cardAction to set the same meta, hence, we will need to combine both layout+datasourceTableRef in one transaction
									dispatch(
										setCardLayoutAndDatasourceTableRef({
											datasourceTableRef,
											layout,
										})(tr),
									);
								}
							}
						} else {
							if (currentState?.datasourceTableRef) {
								dispatch(setDatasourceTableRef(undefined)(tr));
							}
						}

						if (currentState && currentState.provider) {
							// Find requests in this state that weren't in the old one.
							const newRequests = getNewRequests(oldState, currentState);
							// Ask the CardProvider to resolve all new requests.
							const { provider } = currentState;
							newRequests.forEach((request) => {
								/**
								 * Queue each asynchronous resolve request on separate frames.
								 * ---
								 * NB: The promise for each request is queued to take place on separate animation frames. This avoids
								 * the scenario debugged and discovered in EDM-668, wherein the queuing of too many promises in quick succession
								 * leads to the browser's macrotask queue being overwhelmed, locking interactivity of the browser tab.
								 * By using this approach, the browser is free to schedule the resolution of the promises below in between rendering/network/
								 * other tasks as per common implementations of the JavaScript event loop in browsers.
								 */
								const invoke = rafSchedule(() => {
									if (isExperimentEnabled('platform_editor_reduce_memory_leaks')) {
										const index = rafCancellationCallbacks.indexOf(invoke.cancel);
										if (index !== -1) {
											rafCancellationCallbacks.splice(index, 1);
										}
									}
									resolveWithProvider(
										view,
										provider,
										request,
										options,
										pluginInjectionApi?.analytics?.actions,
										pluginInjectionApi?.analytics?.sharedState.currentState()
											?.createAnalyticsEvent ?? undefined,
										currentState?.embedCardTransformers?.embedCardNodeTransformer,
									);
								});
								rafCancellationCallbacks.push(invoke.cancel);
								invoke();
							});
						}

						/**
						 * If there have been any events queued, flush them
						 * so subscribers can now be notified and dispatch
						 * analytics events
						 */
						cardPluginEvents?.flush();
					},

					destroy() {
						// Cancel any outstanding raf callbacks.
						rafCancellationCallbacks.forEach((cancellationCallback) => cancellationCallback());
					},
				};
			},

			props: {
				nodeViews: {
					inlineCard: lazyInlineCardView({
						inlineCardViewProducer,
						isPageSSRed,
						// no need provider here, it's in the inlineCardViewProducer.extraComponentProps
					}),
					blockCard: blockCardNodeView({
						pmPluginFactoryParams,
						actionOptions,
						pluginInjectionApi,
						onClickCallback,
						allowDatasource: options.allowDatasource,
						inlineCardViewProducer,
						isPageSSRed,
						provider,
						CompetitorPrompt: isSSR() ? undefined : options.CompetitorPrompt,
						intl,
						smartCardContext,
					}),
					embedCard: createEmbedCardNodeView({
						allowResizing,
						fullWidthMode,
						pmPluginFactoryParams,
						pluginInjectionApi,
						actionOptions,
						onClickCallback: options.onClickCallback,
						isPageSSRed,
						provider,
						CompetitorPrompt: isSSR() ? undefined : options.CompetitorPrompt,
						intl,
						smartCardContext,
					}),
				},
				...(enableInlineUpgradeFeatures && {
					handleKeyDown: (view: EditorView): boolean => {
						handleAwarenessOverlay(view);
						return false;
					},
					handleClick: (view: EditorView): boolean => {
						handleAwarenessOverlay(view);
						return false;
					},
				}),
			},

			key: pluginKey,
		});
	};
